import PCancelable from 'p-cancelable';

import BLE_UUID_TYPES from '../../consts/ble/bleUuidTypes';
import converter from '../../utils/converter';
import ExponentialBackOff from '../../utils/exponentialBackOff';
import errorService from '../errors/errorService';
import {log} from '../log';
import userDeviceService from '../user/userDeviceService';
import {IConnectProps, IIqosBleClientOptions} from './bleClientTypes';
import {MW_ERRORS} from '../../consts/app/mwErrors';

interface IListener {
    characteristic: BluetoothRemoteGATTCharacteristic;
    handler: (e: any) => void;
}

interface IManagedListeners {
    [index: string]: IListener;
}

interface IConnectDeviceProps extends Omit<IConnectProps, 'onReadingCharacteristics'> {
    services: BluetoothServiceUUID[];
    optionalServices: BluetoothServiceUUID[];
}

interface IGetDeviceProps extends Omit<IConnectDeviceProps, 'onConnect'> {
    onDeviceConnect: TOnConnect;
}

type TOnConnect = (device: BluetoothDevice) => void;

interface IOptions extends Omit<IIqosBleClientOptions, 'onDeviceReconnectSuccess'> {
    onReconnectSuccess: () => Promise<void>;
}

export default class BLEClient {
    private isConnected: boolean;
    private getPermittedDeviceTimeout?: ReturnType<typeof setTimeout>;
    private removeAdvertisementReceivedListener?: () => void;
    private connectPromise?: PCancelable<unknown>;
    private reconnectPromise?: PCancelable<unknown>;
    private isForceDisconnected?: boolean;
    private device?: BluetoothDevice;
    protected server?: BluetoothRemoteGATTServer;
    private primaryService?: BluetoothRemoteGATTService;
    private managedListeners: IManagedListeners = {};
    protected options: IOptions;

    constructor(options: IOptions) {
        this.options = options;
        this.isConnected = false; //device is connected after getPrimaryService
    }

    isDeviceConnected = (): boolean => this.isConnected;

    getPermittedBluetoothDevices = async (onConnect: TOnConnect): Promise<BluetoothDevice[] | null> => {
        const GET_PERMITTED_DEVICE_TIMEOUT_MS = 10 * 1000;

        try {
            const devices = await navigator.bluetooth.getDevices();

            if (devices.length) {
                log.debug(`BLEClient: getPermittedBluetoothDevices, there are ${devices.length} permitted device(s)`);

                this.removeAdvertisementReceivedListener = () => {
                    for (const device of devices) {
                        // @ts-expect-error wrong types
                        device.onadvertisementreceived = null;
                    }
                };

                this.getPermittedDeviceTimeout = setTimeout(() => {
                    this.runRemoveAdvertisementReceivedListener();
                    throw errorService.createError(
                        MW_ERRORS.ERROR_CONNECTION_NOT_FOUND,
                        'getPermittedBluetoothDevices timeout error'
                    );
                }, GET_PERMITTED_DEVICE_TIMEOUT_MS);

                for (const device of devices) {
                    const abortController = new AbortController();

                    await device.watchAdvertisements({signal: abortController.signal});

                    // eslint-disable-next-line no-loop-func
                    device.onadvertisementreceived = async (evt: any) => {
                        log.debug(
                            `BLEClient: getPermittedBluetoothDevices, advertisementreceived, device name: ${evt.target?.name}`
                        );

                        this.clearGetPermittedDevices();
                        abortController.abort();

                        await (evt.device as BluetoothDevice).gatt?.connect();
                        onConnect(evt.device);
                    };
                }
            }

            return devices;
        } catch (e) {
            this.clearGetPermittedDevices();

            log.info(`BLEClient: getPermittedBluetoothDevices error: ${errorService.getErrorMessage(e)}`);
            return null;
        }
    };

    unmount = (): void => {
        this.clearGetPermittedDevices();
    };

    requestDevice = async (
        services: BluetoothServiceUUID[],
        optionalServices: BluetoothServiceUUID[]
    ): Promise<BluetoothDevice | undefined> => {
        log.info('BLEClient: requesting Bluetooth Device');

        try {
            const device = await navigator.bluetooth.requestDevice({
                acceptAllDevices: false,
                filters: [{services}],
                optionalServices,
            });

            return device;
        } catch (e: any) {
            const mwError = errorService.createError(
                MW_ERRORS.ERROR_DEVICE_REQUEST_CANCELED,
                'Device request canceled',
                {
                    originalErrorMessage: e?.message || 'Unknown error',
                }
            );
            throw mwError;
        }
    };

    connectDevice = async ({
        isNewDevice,
        services,
        optionalServices,
        onConnect,
    }: IConnectDeviceProps): Promise<void> => {
        try {
            this.isForceDisconnected = false;

            await this.getDevice({
                isNewDevice,
                services,
                optionalServices,
                onDeviceConnect: async (device: BluetoothDevice) => {
                    try {
                        this.device = device;

                        log.info(`BLEClient: device with id: ${device.id} is connected`);

                        this.options.onDeviceSelect(device.name || 'Unknown Device');

                        device.addEventListener('gattserverdisconnected', this.onDisconnected);
                        device.addEventListener('gattserverforcedisconnected', this.onGattServerForceDisconnected);

                        let server;

                        server = await this.gattConnect(device);
                        this.server = server;

                        await onConnect();
                    } catch (e: any) {
                        log.info(`BLEClient: gattConnect: ${errorService.getErrorMessage(e)}`);

                        const mwError = errorService.createError(
                            MW_ERRORS.ERROR_CONNECTION_NOT_FOUND,
                            'Device request canceled',
                            {
                                originalErrorMessage: e?.message || 'Unknown error',
                            }
                        );
                        throw mwError;
                    }
                },
            });
        } catch (e) {
            log.info(`BLEClient: requestDeviceService failed, error: ${errorService.getErrorMessage(e)}`);
            throw e;
        }
    };

    getDevice = async ({isNewDevice, services, optionalServices, onDeviceConnect}: IGetDeviceProps): Promise<void> => {
        let isRequestDevice = true;

        const isAllowed = false;

        if (!isNewDevice && isAllowed) {
            const permittedDevices = await this.getPermittedBluetoothDevices(onDeviceConnect);

            isRequestDevice = !permittedDevices?.length;
        }

        if (isRequestDevice) {
            const device = await this.requestDevice(services, optionalServices);

            if (!device) return;

            await onDeviceConnect(device);
        }
    };

    gattConnect = (device: BluetoothDevice): Promise<BluetoothRemoteGATTServer> => {
        const connectPromise = new PCancelable((resolve, reject, onCancel) => {
            log.info('BLEClient: connecting to GATT Server');

            device.gatt
                ?.connect()
                .then((server) => resolve(server))
                .catch((error) => {
                    log.info(`BLEClient: gattConnect failed, error: ${errorService.getErrorMessage(error)}`);
                    reject(error);
                });

            onCancel(() => {
                this.removeOnDisconnectedListener(device);
                reject('connectPromise rejected');
            });
        });

        this.connectPromise = connectPromise;

        return connectPromise as Promise<BluetoothRemoteGATTServer>;
    };

    cancelConnect = (): void => {
        try {
            if (this.connectPromise) {
                this.connectPromise.cancel();
                this.connectPromise = undefined;
                this.device = undefined;
                this.server = undefined;
            }
        } catch (e) {
            log.debug(`BLEClient: cancel connect promise failed error: ${e}`);
        }
    };

    getPrimaryService = async (serviceUuid: BLE_UUID_TYPES): Promise<void> => {
        try {
            log.debug('BLEClient: getting Primary Service');

            this.primaryService = await this.server!.getPrimaryService(serviceUuid);

            log.debug('BLEClient: getting Primary Service success');
            this.isConnected = true;
        } catch (e) {
            log.info(`BLEClient: getPrimaryService failed, error: ${errorService.getErrorMessage(e)}`);

            throw e;
        }
    };

    getPrimaryServiceCharacteristic = async (
        characteristicUuid: BLE_UUID_TYPES
    ): Promise<BluetoothRemoteGATTCharacteristic | undefined> => {
        log.debug('BLEClient: getting characteristics');
        return this.primaryService?.getCharacteristic(characteristicUuid);
    };

    addCharacteristicListener = (
        characteristic: BluetoothRemoteGATTCharacteristic | undefined,
        handler: (value: any) => void
    ): void => {
        try {
            if (characteristic) {
                const {uuid} = characteristic;

                this.removeEventListener(uuid);

                this.managedListeners[uuid] = {
                    characteristic,
                    handler: (event: any) => {
                        handler(event.target.value);
                    },
                };

                characteristic.addEventListener('characteristicvaluechanged', this.managedListeners[uuid].handler);
                characteristic.startNotifications().catch((e) => {
                    log.info(`BLEClient: addCharacteristicListener error: ${errorService.getErrorMessage(e)}`);
                });
            } else {
                log.info(`BLEClient: addCharacteristicListener error: characteristic is undefined`);
            }
        } catch (e) {
            log.info(`BLEClient: addCharacteristicListener error: ${errorService.getErrorMessage(e)}`);
        }
    };

    writeValueToCharacteristic = async (
        characteristic: BluetoothRemoteGATTCharacteristic | undefined,
        frame: string,
        throwError: boolean
    ): Promise<void> => {
        if (!this.isDeviceConnected()) return;

        try {
            const frameDecoded = converter.hex2bin(frame);
            const value = frameDecoded.buffer;

            return await characteristic!.writeValue(value);
        } catch (e) {
            log.info(
                `BLEClient: frame: ${frame}, writeValueToCharacteristic error: ${errorService.getErrorMessage(e)}`
            );

            if (throwError) {
                throw e;
            }
        }
    };

    readCharacteristic = async (
        characteristic: BluetoothRemoteGATTCharacteristic | undefined,
        handler: (value: DataView) => void
    ): Promise<void> => {
        if (!this.isDeviceConnected()) return;

        try {
            log.debug('BLEClient: try to read characteristic');
            const value = await characteristic!.readValue();

            handler(value);
        } catch (e) {
            log.info(`BLEClient: read characteristic error: ${errorService.getErrorMessage(e)}`);
            throw e;
        }
    };

    removeCharacteristicListener = (characteristic: BluetoothRemoteGATTCharacteristic | undefined): void => {
        if (characteristic) {
            const {uuid} = characteristic;

            this.removeCharacteristicListenerByUuid(uuid);
        }
    };

    removeCharacteristicListenerByUuid = (uuid: string): void => {
        this.removeEventListener(uuid);
    };

    disconnect = (): void => {
        this.isForceDisconnected = true;
        this.removeAllEventListeners();
        this.removeOnDisconnectedListener(this.device);
        this.disconnectByDevice(this.device);
        this.cancelConnect(); //clear connect promise
        this.cancelReconnect(); //clear reconnect promise if device is already disconnected
        this.clearGetPermittedDevices(); //clear watchAdvertisements
    };

    reconnect = async (): Promise<void> => {
        const fail = (e: any) => {
            const isReconnectCanceled = e?.code === MW_ERRORS.ERROR_RECONNECT_CANCELED.code;

            if (!isReconnectCanceled) {
                log.debug('BLEClient: device reconnection failed');

                this.options.onReconnectFail?.();
            }
        };

        try {
            const device = this.device;

            if (device) {
                const toTry = <BluetoothRemoteGATTServer>(
                    attemptNumber: number
                ): Promise<BluetoothRemoteGATTServer> => {
                    const reconnectPromise = new PCancelable((resolve, reject, onCancel) => {
                        device.gatt
                            ?.connect()
                            .then((server) => resolve(server))
                            .catch((e) => reject(e));

                        onCancel(() => {
                            log.debug('BLEClient: device reconnection canceled');
                            reject(
                                errorService.createError(
                                    MW_ERRORS.ERROR_RECONNECT_CANCELED,
                                    'Reconnection was canceled'
                                )
                            );
                        });
                    });

                    this.reconnectPromise = reconnectPromise;

                    log.debug(`BLEClient: try to reconnect device, attempt: ${attemptNumber}`);

                    return reconnectPromise as Promise<BluetoothRemoteGATTServer>;
                };

                const success = (server: BluetoothRemoteGATTServer) => {
                    log.info(`BLEClient: device with id: ${device.id} is reconnected`);
                    this.reconnectPromise = undefined;
                    this.device = device;
                    this.server = server;

                    this.options.onReconnectSuccess();
                };

                //300 ~= 10 min
                const retriesCount = userDeviceService.isMobileAndroidDevice() ? 300 : 3;

                const exponentialBackOff = new ExponentialBackOff({
                    max: retriesCount,
                    delay: () => 2000,
                    toTry,
                    success,
                    fail,
                });

                exponentialBackOff.run();
            }
        } catch (e: any) {
            fail(e);
        }
    };

    cancelReconnect = (): boolean => {
        try {
            if (this.reconnectPromise) {
                this.reconnectPromise.cancel();
                this.reconnectPromise = undefined;
                this.device = undefined;
                this.server = undefined;

                return true;
            }
        } catch (e) {
            log.info(`BLEClient: cancel reconnect promise failed error: ${errorService.getErrorMessage(e)}`);
        }

        return false;
    };

    getDeviceName = (): string => {
        return this.device?.name || '';
    };

    private runRemoveAdvertisementReceivedListener = () => {
        this.removeAdvertisementReceivedListener?.();
        this.removeAdvertisementReceivedListener = undefined;
    };

    private clearGetPermittedDevices = (): void => {
        this.runRemoveAdvertisementReceivedListener();
        clearTimeout(this.getPermittedDeviceTimeout!);
    };

    private onGattServerForceDisconnected = () => {
        log.info(`BLEClient: "gattserverforcedisconnected" is fired`);

        this.options.onForcedDisconnect();
        this.onDisconnected();
        // IA - call onDisconnected directly, because onDisconnect
        // is not called after gattserverforcedisconnected event
    };

    private onDisconnected = () => {
        const {isForceDisconnected} = this;

        //isForceDisconnected - force disconnect by app or unpair click by user
        log.info(`BLEClient: device disconnected. isForceDisconnected: ${isForceDisconnected}`);

        this.isConnected = false;
        this.options.onDisconnected(isForceDisconnected);
        this.removeAllEventListeners();

        if (isForceDisconnected) {
            this.removeOnDisconnectedListener(this.device);
            this.device = undefined;
        } else {
            this.reconnect();
        }
    };

    private removeOnDisconnectedListener = (device?: BluetoothDevice): void => {
        device?.removeEventListener('gattserverdisconnected', this.onDisconnected);
        device?.removeEventListener('gattserverforcedisconnected', this.onGattServerForceDisconnected);
    };

    private removeAllEventListeners = () => {
        if (this.managedListeners) {
            Object.keys(this.managedListeners).forEach(this.removeEventListener);
        }

        this.managedListeners = {};
    };

    private removeEventListener = (uuid: string): void => {
        try {
            const listener = this.managedListeners[uuid];

            if (listener) {
                const {characteristic, handler} = listener;

                characteristic.removeEventListener('characteristicvaluechanged', handler);
                delete this.managedListeners[uuid];
            }
        } catch (e) {
            log.info(`BLEClient: removeEventListener error: ${errorService.getErrorMessage(e)}`);
        }
    };

    private disconnectByDevice = (device?: BluetoothDevice): void => {
        if (device) {
            log.info(`BLEClient: disconnect device with id: ${device.id}`);
            device.gatt?.disconnect();
        }
    };
}
